import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import * as remoteApi from 'api/remote';
import theme from 'theme';
import { useTranslation } from 'react-i18next';
import { DragHandle, WidgetContainer, WidgetContent, WidgetHeader } from '.';
import { DashboardWidget, Workspace } from 'types';
import KoalaIcon from 'koala/components/Icons';
import { useQuery } from 'react-query';
import queryKeys from 'config/queryKeys';
import { shallowEqual, useSelector } from 'react-redux';
import KoalaLoader from 'koala/components/Loader';
import { CustomTermKey, translate } from 'utils/customTermUtils';
import {
  VictoryAxis,
  VictoryChart,
  VictoryHistogram,
  VictoryLabel,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from 'victory';
import { addMonths, addWeeks, format, parseISO, startOfMonth, startOfWeek, subWeeks } from 'date-fns';
import { getLocaleMonth } from 'utils/dateUtils';

const Container = styled.div`
  height: 18rem;
  display: flex;
  width: 100%;
  height: 90%;
  max-height: 100%;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.x4};
  height: 100%;
  flex-direction: column;

  @media ${theme.devices.mobile} {
    max-height: 25rem;
  }
`;

const GraphContainer = styled.div`
  max-width: 100%;
  max-height: 15rem;
`;

const LabelTooltip = () => {
  return (
    <VictoryTooltip
      cornerRadius={4}
      flyoutStyle={{
        fill: 'rgba(255, 255, 255, 1)',
        stroke: theme.colors.blockBorder,
      }}
      dy={-12}
      flyoutPadding={{ top: 10, bottom: 10, left: 15, right: 15 }}
      constrainToVisibleArea
      style={[
        {
          fontWeight: '900',
          textTransform: 'uppercase',
          fill: theme.colors.subtleText,
          fontSize: '10px',
          fontFamily: theme.font.fontFamily,
          marginBottom: 0,
        },
        {
          fontWeight: '600',
          fill: theme.colors.black,
          fontSize: '12px',
          fontFamily: theme.font.fontFamily,
        },
      ]}
      labelComponent={<VictoryLabel lineHeight={1.4} />}
    />
  );
};

interface Props {
  widget: DashboardWidget;
}

interface InitiativeActivity {
  initiative_comments: number;
  week: string;
}
interface GraphData {
  x: Date;
}

function InitiativeUpdateChartWidget(props: Props) {
  const { widget } = props;
  const currentWorkspace: Workspace = useSelector((state: any) => state.session.currentWorkspace, shallowEqual);
  const { t, i18n } = useTranslation();
  const chartRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 200, height: 200 });

  useEffect(() => {
    const handleResize = (entries: any) => {
      for (let entry of entries) {
        if (entry.target === chartRef.current) {
          setDimensions({
            width: entry.contentRect.width,
            height: entry.contentRect.height,
          });
        }
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    if (chartRef.current) {
      resizeObserver.observe(chartRef.current);
    }

    // Cleanup observer on unmount
    return () => {
      if (chartRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        resizeObserver.unobserve(chartRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRef.current]);

  const widgetTitle: string = widget.title
    ? widget.title
    : t('workspaceDashboards.initiative-update', {
        initiative: translate(currentWorkspace, CustomTermKey.INITIATIVE, 1),
      });

  const refDate = Date.now();
  const bins: Date[] = [];
  for (let i = 24; i >= 0; i--) {
    const bin = subWeeks(startOfWeek(refDate), i);
    bins.push(bin);
  }
  const lastBin = addWeeks(startOfWeek(refDate), 1);
  bins.push(lastBin);

  const firstBin = startOfMonth(subWeeks(startOfWeek(refDate), 24));
  const months: Date[] = [
    addMonths(firstBin, 1),
    addMonths(firstBin, 2),
    addMonths(firstBin, 3),
    addMonths(firstBin, 4),
    addMonths(firstBin, 5),
    addMonths(firstBin, 6),
  ];

  const queryKeyInitiativesCommented = [
    queryKeys.currentWorkspace,
    currentWorkspace.slug,
    'initiatives_commented_activity',
  ];
  const staleTime = queryKeys.staleTime;
  const {
    isFetching: isFetchinInitiativesCommented,
    isLoading: isLoadingInitiativesCommented,
    data: initiativesCommentedResponse,
  } = useQuery(queryKeyInitiativesCommented, remoteApi.fetchWorkspaceInitiativesCommentedActivity, {
    staleTime,
  });

  const initiativesCommentedActivity: InitiativeActivity[] = initiativesCommentedResponse?.data || [];

  const initiativesData: GraphData[] = [];
  initiativesCommentedActivity.forEach((activity) => {
    const initiativesCommentedCount = activity.initiative_comments || 0;
    for (let i = 0; i < initiativesCommentedCount; i++) {
      initiativesData.push({
        x: parseISO(activity.week),
      });
    }
  });

  if (!isLoadingInitiativesCommented && !isFetchinInitiativesCommented && !initiativesCommentedActivity) {
    return (
      <WidgetContainer>
        <WidgetHeader>
          <DragHandle className="drag-handle">
            <KoalaIcon iconName="grab" iconSize="small" />
          </DragHandle>
          <p className="widget-type">{widgetTitle}</p>
        </WidgetHeader>
        <WidgetContent>
          <p>{t('workspaceDashboards.widgetError')}</p>
        </WidgetContent>
      </WidgetContainer>
    );
  }

  return (
    <WidgetContainer>
      <WidgetHeader>
        <DragHandle className="drag-handle">
          <KoalaIcon iconName="grab" iconSize="small" />
        </DragHandle>
        <p className="widget-type">{widgetTitle}</p>
      </WidgetHeader>
      <WidgetContent>
        {isFetchinInitiativesCommented && <KoalaLoader />}

        <Container className="panel-checkins-chart" ref={chartRef}>
          <GraphContainer>
            <VictoryChart
              containerComponent={<VictoryVoronoiContainer />}
              width={dimensions.width}
              height={dimensions.height}
              padding={{ top: 0, bottom: 23, left: 0, right: 5 }}
            >
              <VictoryHistogram
                data={initiativesData}
                bins={bins}
                labels={({ datum }: any) => {
                  return [format(datum.x, 'd MMM yyyy'), datum.y];
                }}
                labelComponent={LabelTooltip()}
                cornerRadius={4}
                binSpacing={2}
                style={{
                  data: {
                    fill: theme.colors.B50,
                    strokeWidth: 0,
                    width: 25,
                  },
                  labels: {
                    fill: theme.colors.subtleText,
                    fontSize: 10,
                  },
                }}
              />
              <VictoryAxis
                tickValues={months}
                tickCount={months.length}
                tickFormat={(x) => {
                  return getLocaleMonth(x, i18n);
                }}
                style={{
                  axis: { stroke: theme.colors.blockBorder },
                  tickLabels: {
                    fill: theme.colors.subtleText,
                    fontSize: 12,
                  },
                }}
              />
            </VictoryChart>
          </GraphContainer>
        </Container>
      </WidgetContent>
    </WidgetContainer>
  );
}

export default InitiativeUpdateChartWidget;
